import { GLOBAL_CONSTANTS } from 'assets/js/utils/constants'
import lottie from 'lottie-web'

const SELECTORS = {
  scrubber: '.js-audio__playhead',
  player: 'audio',
  controls: '.js-audio-controls',
  name: '.js-audio-name',
  playButton: '.js-audio-button',
  buttonIcon: '.js-button__icon',
  timestamp: '.js-audio__timestamp',
  volumeButton: '.js-audio__volume-btn',
  volumeScrubber: '.js-audio__volume',
  volumeScrubberWrapper: '.js-scrubber-wrapper',
  transcript: '.js-audio__transcript',
  transcriptButton: '.js-audio__transcript-btn'
}

class AudioPlayer {
  constructor (element) {
    this.element = element
    this.scrubber = this.element.querySelector(SELECTORS.scrubber)
    this.player = this.element.querySelector(SELECTORS.player)
    this.controls = this.element.querySelectorAll(SELECTORS.controls)
    this.name = this.element.querySelector(SELECTORS.name)
    this.playButton = this.element.querySelector(SELECTORS.playButton)
    this.buttonIcon = this.playButton.querySelector(SELECTORS.buttonIcon)
    this.timestamp = this.element.querySelector(SELECTORS.timestamp)
    this.volumeButton = this.element.querySelector(SELECTORS.volumeButton)
    this.volumeScrubber = this.element.querySelector(SELECTORS.volumeScrubber)
    this.volumeScrubberWrapper = this.element.querySelector(SELECTORS.volumeScrubberWrapper)
    this.transcript = this.element.querySelector(SELECTORS.transcript)
    this.transcriptButton = this.element.querySelector(SELECTORS.transcriptButton)
    this.emitter = GLOBAL_CONSTANTS.UTILS.EMITTER
    this.lottie = this.loadAnimation()

    this.transcriptVisible = false
    this.storedVolume = 100
    this.seeking = false

    this.init()
  }

  init() {
    this.playButton.addEventListener('click', () => this.togglePlayPause())

    this.player.addEventListener('loadstart', () => this.toggleButton())
    this.player.addEventListener('play', () => {
      this.toggleButton()
      this.showControls()
    })
    this.player.addEventListener('pause', () => this.toggleButton())
    this.player.addEventListener('ended', () => {
      this.toggleButton()
      this.hideControls()
    })

    this.player.addEventListener('timeupdate', () => {
      this.updateScrubber(this.getProgress())
      this.displayDuration()
    })

    // If the scrubber exists, attach the necessary events
    if (this.scrubber) {
      this.scrubber.addEventListener('change', (e) => this.seekTo(e.target.value))
      this.scrubber.addEventListener('input', (e) => this.updateScrubber(e.target.value, true))
      this.scrubber.addEventListener('mousedown', () => {
        this.seeking = true
      })
      this.scrubber.addEventListener('mouseup', () => {
        this.seeking = false
      })
    }

    // If the volume scrubber/button exists, attach the necessary events
    if (this.volumeScrubber && this.volumeButton) {
      this.volumeButton.addEventListener('click', (e) => {
        if (e.target !== this.volumeScrubber) {
          this.toggleMute()
        }
      })
      this.volumeScrubber.addEventListener('change', (e) => {
        this.storedVolume = e.target.value
      })
      this.volumeScrubber.addEventListener('input', (e) => this.updateVolume(e.target.value))
    }

    // If a transcript exists, attach the necessary events
    if (this.transcriptButton && this.transcript) {
      this.transcriptButton.addEventListener('click', () => this.toggleTranscript())
    }

    if (!('audioplayers' in window.unlocked)) {
      window.unlocked.audioplayers = {}
    }

    window.unlocked.audioplayers[this.element.id] = {
      pause: () => {
        this.player.pause()
      },
      play: () => {
        this.player.play()
      },
      reset: () => {
        this.player.pause()
        this.seekTo(0)
        this.hideControls()
      },
      htmlPlayer: this.player,
      state: () => this.player.paused ? 'paused' : 'playing'
    }
  }

  // Play/pause the audio
  togglePlayPause() {
    if (this.player.paused) {
      this.player.play()
    } else {
      this.player.pause()
    }
  }

  // Play/pause the audio
  toggleButton() {
    if (this.player.paused) {
      this.lottie.goToAndStop(0)
      this.playButton.ariaPressed = false
    } else {
      this.lottie.play()
      this.playButton.ariaPressed = true
    }
  }

  // Update the current timestamp
  displayDuration() {
    if (!this.timestamp) return

    const time = this.player.currentTime
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    const returnedSeconds = seconds < 10 ? `0${seconds}` : `${seconds}`
    this.timestamp.innerHTML = `${minutes}:${returnedSeconds}`
  }

  // Update the playback scrubber
  updateScrubber(progress, manual) {
    if (!this.seeking || manual) {
      this.element.style.setProperty('--audio-progress', `${progress}%`)
    }
  }

  // Get the current progress
  getProgress() {
    const currentTime = this.player.currentTime
    const totalTime = this.player.duration
    return currentTime / totalTime * 100
  }

  // Seek to the specified point in the audio
  seekTo(value) {
    if (!this.scrubber) return

    const totalTime = this.player.duration
    this.player.currentTime = value * totalTime / 100
    this.updateScrubber(this.getProgress())
  }

  updateVolume(value) {
    if (!this.volumeScrubber) return

    this.element.style.setProperty('--audio-volume', `${value}%`)
    this.player.volume = value / 100
    if (this.player.muted) {
      this.toggleMute()
    }

    if (this.player.volume === 0) {
      this.volumeButton.classList.add('audio__volume-btn--muted')
    } else if (!this.player.muted) {
      this.volumeButton.classList.remove('audio__volume-btn--muted')
    }
  }

  toggleMute() {
    this.player.muted = !this.player.muted

    if (this.player.muted) {
      this.volumeButton.classList.add('audio__volume-btn--muted')
      this.volumeButton.setAttribute('aria-label', 'Unmute')
      this.element.style.setProperty('--audio-volume', '0%')
      this.volumeScrubber.value = 0
    } else {
      this.volumeButton.classList.remove('audio__volume-btn--muted')
      this.volumeButton.setAttribute('aria-label', 'Mute')
      this.volumeScrubber.value = this.player.volume * 100
      this.updateVolume(this.player.volume * 100)
    }
  }

  toggleTranscript() {
    if (!this.transcriptButton || !this.transcript) return

    this.transcriptVisible = !this.transcriptVisible

    this.transcriptButton.ariaExpanded = this.transcriptVisible
    this.transcript.classList.toggle('-hide')
    this.transcript.ariaHidden = !this.transcriptVisible

    this.emitter.emit(GLOBAL_CONSTANTS.EVENTS.AUDIO_TRANSCRIPT_TOGGLE, {
      element: this.element,
      isVisible: this.transcriptVisible
    })
  }

  loadAnimation() {
    const animation = lottie.loadAnimation({
      container: this.buttonIcon,
      path: `${unlocked.theme_dir}/assets/lottie/AudioButton.json`,
      renderer: 'svg',
      loop: true,
      autoplay: false
    })

    if (!animation.isLoaded) {
      animation.addEventListener('DOMLoaded', () => animation.goToAndStop(0))
    }

    return animation
  }

  showControls() {
    this.controls.forEach(control => control.classList.remove('-hide'))
    this.name?.classList.add('-hide')
  }

  hideControls() {
    this.controls.forEach(control => control.classList.add('-hide'))
    this.name?.classList.remove('-hide')
    if (this.transcriptVisible) {
      this.toggleTranscript()
    }
  }
}

export default {
  selector: '.js-audio-player',
  Source: AudioPlayer
}
